import {
  ApolloClient,
  InMemoryCache,
  from,
  type NormalizedCacheObject,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import createUploadLink from "apollo-upload-client/createUploadLink.mjs";
import {
  createStaleWorkspaceLink,
  getWorkspaceHeader,
  registerWorkspaceScopedClient,
} from "@ticketon/marketing/workspace";
import { sessionManager } from "@/auth/sessionBridge";
import { buildLocaleHeader } from "@/i18n/localeHeader";

const MARKETING_ADMIN_GRAPHQL_URI = "/platform/admin/marketing/graphql";

let marketingAdminClient: ApolloClient<NormalizedCacheObject> | null = null;

function getAuthHeader(): Record<string, string> {
  const session = sessionManager.getSession();
  if (session?.token) {
    return { Authorization: `Bearer ${session.token}` };
  }
  return {};
}

/**
 * The marketing schema, used only for the workspace asset library the landing page editor
 * picks images from. Everything travel-specific stays on the travel client; this one exists
 * because assets live in the user's marketing workspace and the request has to carry its id.
 */
export function getMarketingAdminClient(): ApolloClient<NormalizedCacheObject> {
  if (!marketingAdminClient) {
    const apiEndpoint = import.meta.env.VITE_API_ENDPOINT.endsWith("/")
      ? import.meta.env.VITE_API_ENDPOINT.slice(0, -1)
      : import.meta.env.VITE_API_ENDPOINT;

    const httpLink = createUploadLink({
      uri: `${apiEndpoint}${MARKETING_ADMIN_GRAPHQL_URI}`,
    });

    const authLink = setContext((_, { headers }) => ({
      headers: {
        ...(headers || {}),
        ...buildLocaleHeader(),
        ...getAuthHeader(),
        ...getWorkspaceHeader(),
        // Required by HotChocolate's CSRF protection for multipart (file upload) requests.
        "GraphQL-preflight": "1",
      },
    }));

    marketingAdminClient = new ApolloClient({
      link: from([createStaleWorkspaceLink(), authLink, httpLink]),
      cache: new InMemoryCache(),
      defaultOptions: {
        watchQuery: {
          fetchPolicy: "cache-and-network",
        },
      },
    });

    registerWorkspaceScopedClient(marketingAdminClient);
  }
  return marketingAdminClient;
}

export function resetMarketingAdminClient(): void {
  marketingAdminClient = null;
}
